import { environment } from '../../../environments/environment';

/** Authenticated SSE stream under `{baseUrl}/{apiVersion}/`. */
export const REALTIME_STREAM_PATH = environment.realtimeStreamPath || 'events/stream';

export const REALTIME_STREAM_URL = `${environment.baseUrl}/${environment.apiVersion}/${REALTIME_STREAM_PATH}`;

/**
 * Server handshake on `/events/stream`.
 * FE treats this as stream-ready; not a business notification.
 */
export const SSE_CONNECTED_EVENT = 'connected';

/** POST target for the liveness probe (CWS-1377). */
export const REALTIME_PROBE_PATH = 'events/probe';

export const REALTIME_PROBE_URL = `${environment.baseUrl}/${environment.apiVersion}/${REALTIME_PROBE_PATH}`;

/**
 * Probe echo. The server writes this back down the *same* stream the nonce was
 * issued for, which is the only evidence that this connection still carries
 * bytes — a carrier NAT reap is silent, and a side request proves nothing
 * because it rides a different TCP flow.
 */
export const SSE_PROBE_EVENT = 'sse-probe';

/**
 * Window CustomEvents dispatched by the Android shell (`WebViewWebEvents` in
 * app-android-carwash). Not SSE events — these are native -> JS lifecycle signals.
 *
 * The APK and this app ship on separate cadences, so these must stay optional in both
 * directions: a tablet on an older APK simply never fires them and the stream falls back to
 * `visibilitychange` / `online` / the liveness watchdog.
 */
export const ANDROID_APP_RESUMED_EVENT = 'android_app_resumed';

/** Detail: `{ available: boolean }`. */
export const ANDROID_NETWORK_CHANGED_EVENT = 'android_network_changed';

/**
 * Server-initiated control event: the backend enforces a bounded SSE token/
 * session lifetime and closes the stream when it is exceeded. The client treats
 * this as a signal to re-authenticate (pick up a fresh token) and reconnect —
 * it is NOT a business notification.
 */
export const SSE_SESSION_EXPIRED_EVENT = 'session-expired';

/** Deploy wake-up (same event name as legacy Pusher `app_version_ready`). */
export const DEPLOYMENT_SSE_EVENT = 'app_version_ready';

/** Tunnel wash list wake-up (same as legacy Pusher `vehicles-{id}` / `vehicle_washes`). */
export const VEHICLE_WASHES_SSE_EVENT = 'vehicle_washes';

/** Axis LPR row correction (same as legacy Pusher `lpr-axis-entry-updated`). */
export const LPR_AXIS_ENTRY_UPDATED_SSE_EVENT = 'lpr-axis-entry-updated';

/**
 * Cross-session selected-lane sync (was Pusher `selected-lane-changed`).
 * PER-USER SSE event: the backend publishes it on the `user-{id}` channel and the
 * realtime-api only fans it out to that user's own authenticated streams, so every
 * tablet/tab of the SAME user re-aligns its header lane + lane-dependent UI.
 */
export const SELECTED_LANE_CHANGED_SSE_EVENT = 'selected-lane-changed';

/**
 * Background order-processing completion (location-scoped).
 * Emitted by order-api after an order is created or processed in the background
 * so the admin/staff order table can refresh without polling. Payload should
 * include at least `location_id`, and `lane_id` when the order is tied to a lane
 * (null/omitted = order belongs to all lanes for the lane filter).
 */
export const ORDER_PROCESSED_SSE_EVENTS = ['order_processed', 'order-processed'] as const;

/**
 * Employee attendance check-in / check-out (location-scoped and/or admin user-scoped).
 * Emitted by attendant-api after markAttendance so the admin employee list can
 * update Attendance (In / Out / Offline) without a hard reload.
 * Payload: `employee_id`, `work_status`, optional `location_id` / `admin_id` / `action`.
 */
export const ATTENDANCE_STATUS_CHANGED_SSE_EVENTS = [
  'attendance_status_changed',
  'attendance-status-changed',
] as const;

/** Payment status events (was Pusher `order-{userId|locationId}`). */
export const PAYMENT_SSE_EVENTS = [
  'payment_done',
  'payment_failed',
  'payment_succeeded',
  'order_payment_done',
] as const;

/** Modal-opening LPR events (was Pusher `location-ID-*`). */
export const LPR_MODAL_SSE_EVENTS = [
  'fleet-vehicle-found',
  'invalid-license-plate',
  'order-success',
  'create-payment-request',
  'create-customer-request',
  'create-order-request',
  'redeem-prepaid-purchase',
  'detailing-appointment-found',
  'active-subscription',
  'paused-subscription',
  'way-single-wash',
  'way-active-subscription',
  'way-purchase-selection',
  'way-purchase-unredeemable',
  'way-subscription-already-redeemed-today',
  'multi-eligible-selection',
] as const;

/**
 * Payload flag svc-order sets when the read came from a lane whose camera is marked
 * inactive. The event name is unchanged, so the row looks and behaves like any other
 * notification and staff can still open it to create the order. The flag only stops
 * the app from acting on its own: no modal auto-open and no lane-attention toast.
 */
export const CAMERA_MONITOR_ONLY_PAYLOAD_FLAG = 'camera_monitor_only';

/** Cash reconciliation toasts/refresh (was Pusher `reconciliation-{userId}`). */
export const RECONCILIATION_SSE_EVENTS = [
  'admin-opening_balance_updated',
  'admin-closing_balance_updated',
  'admin-extra_cash_added',
  'entry-not-closed',
  'closing-missing',
  'eod-summary',
] as const;

/** @deprecated Use RECONCILIATION_SSE_EVENTS */
export const LPR_RECONCILIATION_SSE_EVENTS = RECONCILIATION_SSE_EVENTS;

export const LPR_AXIS_IMAGES_READY_SSE_EVENT = 'lpr-axis-images-ready';

/**
 * Stackable staff toast (does not open LPR modal). Was Pusher `lane-attention` on `location-ID-*`.
 * Location-wide; not filtered by header lane — callers should still apply location allow-list.
 */
export const LPR_LANE_ATTENTION_SSE_EVENT = 'lane-attention';

/**
 * CWS-1650: lane-attention reason for a refused vendor member. Shown only to attendants
 * on the other lanes (svc-order laneAttentionPublisher VENDOR_MEMBER_REFUSED).
 */
export const VENDOR_MEMBER_REFUSED_REASON = 'vendor_member_refused';

/**
 * Tunnel wash state of a board row (CWS-1917 contract §4), on the same `vehicles-{locationId}`
 * channel as `vehicle_washes`. Payload: `{ lpr_entry_id, tunnel_wash_status,
 * tunnel_package_name, tunnel_completed_at }`.
 */
export const TUNNEL_WASH_STATE_SSE_EVENT = 'tunnel_wash_state';
